import { utils, writeFile } from 'xlsx';
import groupInitiativesBy from '../group/groupInitiatives';
import groupMilestonesBy from '../group/groupMilestones';
import groupWorkstreamActionsBy from '../group/groupActionItems';
import { getCancellationDate, getQualitativeKPI, L1OriginalPlanDate, L2OriginalPlanDate, L3OriginalPlanDate, L4OriginalPlanDate, L5OriginalPlanDate } from '../Sort/checkDate/getDate';
import groupInitiativesByYearActual from '../Sort/groupInitiativesByYearActual';
import getMilestonePlanningSummary from '../filters/getPlanningSummary';
import getSimplifiedPlanningSummary from '@/components/Kanban/Workbook/getMilestonePlanningSummary';
import filterMilestoneByPlanningSummary from '../filters/filterMilestonesByPlanningSummary';
import planningSummarySimplified from '../planningSummarySimplified';

// ===== IMPROVED DATE FORMATTING FUNCTIONS =====
const applyDateFormats = (worksheet) => {
    if (!worksheet || !worksheet['!ref']) return;

    const range = utils.decode_range(worksheet['!ref']);

    // Get header row to identify date columns
    const headers = [];
    for (let C = 0; C <= range.e.c; C++) {
        const headerCell = worksheet[utils.encode_cell({ r: 0, c: C })];
        headers[C] = headerCell?.v?.toString().toLowerCase() || '';
    }

    // Keywords that indicate a column contains dates
    const dateKeywords = [
        'date', 'planned', 'actual', 'original', 'submitted', 'created', 'updated',
        'cancellation', 'start', 'completion', 'due', 'L0', 'L1', 'L2', 'L3', 'L4', 'L5'
    ];

    // Check each column
    for (let C = 0; C <= range.e.c; C++) {
        // Check if this column might be a date column
        const isDateColumn = dateKeywords.some(keyword => headers[C].includes(keyword));

        if (isDateColumn) {
            for (let R = 1; R <= range.e.r; R++) {
                const cellAddress = utils.encode_cell({ r: R, c: C });
                const cell = worksheet[cellAddress];

                // If it's a number that could be a date (between 40000-50000 for recent years)
                if (cell && cell.t === 'n' && cell.v > 40000 && cell.v < 50000) {
                    cell.z = 'yyyy-mm-dd'; // Format as date without time
                }
            }
        }
    }
};

// In your download handler, replace the formatDateSafely function
const formatDateSafely = (dateInput) => {
    if (!dateInput) return null;

    try {
        let d;
        if (dateInput instanceof Date) {
            d = dateInput;
        } else {
            d = new Date(dateInput);
        }

        // Check if date is valid
        if (isNaN(d.getTime())) return null;

        // Check if it's the epoch (1970-01-01) - return null to keep cell blank
        if (d.getUTCFullYear() === 1970 && d.getUTCMonth() === 0 && d.getUTCDate() === 1) {
            return null;
        }

        // Use UTC components to calculate Excel serial number
        const year = d.getUTCFullYear();
        const month = d.getUTCMonth();
        const day = d.getUTCDate();

        // Calculate Excel serial number using UTC
        // Formula: = (date - DATE(1900,1,1)) + 2 (for Excel's leap year bug)
        const excelEpoch = new Date(Date.UTC(1900, 0, 1));
        const currentDate = new Date(Date.UTC(year, month, day));
        const diffTime = currentDate.getTime() - excelEpoch.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        // Excel incorrectly treats 1900 as a leap year, so add 2 for dates after 1900-02-28
        return diffDays + 2;
    } catch (error) {
        console.error('Error formatting date:', error);
        return null;
    }
};
// ===== END OF DATE FORMATTING FUNCTIONS =====

const handleDownloadData = (
    mainExportData,
    mainExportType,
    allUsersData,
    allInitiativesData,
    allWorkstreamsDefinitions,
    allHistoryData,
    selectedRange,
    groupBy = null,
    determineStage,
    calculateNetRecurringBenefit,
    calculateOneTimeBenefit,
    calculateOneTimeImplementationCost,
    l3PlanDate,
    l4PlanDate,
    InitiativePlanningSummary,
    checkWorkstreamLeadApprovalStatus,
    checkWorkstreamSponsorApprovalStatus,
    checkTOFinanceApprovalStatus,
    getNetRecurringBenefitCatagory,
    l2PlanDate,
    l5PlanDate,
    L0PlanDate,
    L1PlanDate,
    l2ActualDate,
    l3ActualDate,
    l4ActualDate,
    l5ActualDate,
    LLatestActualDate,
    submittedForL1ApprovalPlanDate,
    submittedForL2ApprovalPlanDate,
    submittedForL3ApprovalPlanDate,
    submittedForL4ApprovalPlanDate,
    submittedForL5ApprovalPlanDate,
    getNetRecurringBenefitCatagoryL0Plus,
    getNetRecurringBenefitCatagoryL1Plus,
    getNetRecurringBenefitCatagoryL2Plus,
    getNetRecurringBenefitCatagoryL3Plus,
    getNetRecurringBenefitCatagoryL4Plus,
    getComplianceStatusForGrouping,
    sortInitiativesByActuals,
    checkStrategyAnalystApprovalStatus,
    sortInitiativesByActualImpact,
    getImplementationCostCatagory,
    determinePlanningSummary,
    getMilestoneBinValue,
    initialColumns = [],
    visibleColumns = [],
    milestoneInitialColumns = [],
    milestoneVisibleColumns = [],
    actionItemInitialColumn = [],
    actionItemVisibleColumns = [],
    subsectors
) => {
    if (!Array.isArray(initialColumns)) {
        console.error('initialColumns must be an array', initialColumns);
        return;
    }
    if (!Array.isArray(visibleColumns)) {
        console.error('visibleColumns must be an array', visibleColumns);
        return;
    }

    // Ensure all data arrays are arrays
    mainExportData = Array.isArray(mainExportData) ? mainExportData : [];
    allUsersData = Array.isArray(allUsersData) ? allUsersData : [];
    allInitiativesData = Array.isArray(allInitiativesData) ? allInitiativesData : [];
    allWorkstreamsDefinitions = Array.isArray(allWorkstreamsDefinitions) ? allWorkstreamsDefinitions : [];
    allHistoryData = Array.isArray(allHistoryData) ? allHistoryData : [];

    const getDateForComplianceCheck = (initiative, stageToCheck) => {
        if (!initiative || !initiative.timeline) return null;
        return initiative.timeline.find((entry) => entry.stage === stageToCheck)?.date?.plan;
    };
    const getSectorName = (impact) => {
        const subsector = subsectors.find((sub) => sub?._id === impact?.subsector);
        return subsector ? subsector?.sector?.sectorName : 'Not assigned';
    }
    const getSubsectorSpecificName = (impact) => {
        const subsector = subsectors.find((sub) => sub?._id === impact.subsector);
        return subsector ? subsector?.subsectorName : 'Not assigned';
    }
    const processSectorArray = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0 || !arr[0]) return '';
        return arr[0].sectorName || '';
    };
    const processSubsectorArray = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0 || !arr[0]) return '';
        return arr[0].subsectorName || '';
    };
    const getComplianceStatus = (initiative, stageToCheck) => {
        const targetDate = getDateForComplianceCheck(initiative, stageToCheck);
        if (!targetDate) {
            return ['<not set>'];
        }

        const plannedDate = new Date(targetDate);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        plannedDate.setHours(0, 0, 0, 0);
        const daysDifference = Math.ceil(
            (plannedDate.getTime() - currentDate.getTime()) / (1000 * 3600 * 24)
        );

        const currentStage = determineStage(initiative);
        const delayIndicators = [];

        const isDelayed = () => {
            if (stageToCheck === 'L3') {
                return (
                    ['L0', 'Submitted for L1 Approval', 'L1', 'Submitted for L2 Approval', 'L2'].includes(
                        currentStage
                    ) && daysDifference > 0
                );
            }
            if (stageToCheck === 'L4') {
                return (
                    [
                        'L0',
                        'Submitted for L1 Approval',
                        'L1',
                        'Submitted for L2 Approval',
                        'L2',
                        'Submitted for L3 Approval',
                        'L3',
                    ].includes(currentStage) && daysDifference > 0
                );
            }
            return false;
        };

        const isAccelerated = () => {
            return currentStage === stageToCheck && daysDifference < 0;
        };

        const isUnchanged = () => {
            return daysDifference === 0;
        };

        if (isAccelerated()) return ['Accelerated'];
        if (isDelayed()) {
            const delayDays = Math.abs(daysDifference);
            if (delayDays <= 7) {
                delayIndicators.push('Delayed (1 to 7 days)');
            } else if (delayDays <= 14) {
                delayIndicators.push('Delayed (8 to 14 days)');
            } else if (delayDays <= 30) {
                delayIndicators.push('Delayed (15 to 30 days)');
            } else if (delayDays > 30) {
                delayIndicators.push('Delayed (more than 30 days)');
            }
            return delayIndicators;
        }
        if (isUnchanged()) return ['Unchanged'];
        if (daysDifference > 0) return ['Accelerated'];
        return ['<not set>'];
    };

    const processPersonArray = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0 || !arr[0]) return '';
        return `${arr[0].firstName || ''} ${arr[0].lastName || ''}`.trim();
    };

    const processSubworkstreamArray = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0 || !arr[0]) return '';
        return arr[0].subworkstreamName || '';
    };

    const processWorkstreamArray = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0 || !arr[0]) return '';
        return arr[0].workstreamName || '';
    };

    const processCrossFunctionalWorkstreamArray = (arr) => {
        if (!arr || !Array.isArray(arr) || arr.length === 0 || !arr[0]) return '';
        return arr[0].subworkstreamName || '';
    };

    const getLatestUpdateEvent = (actionItem) => {
        if (!actionItem || !actionItem.activity || !Array.isArray(actionItem.activity) || actionItem.activity.length === 0) {
            return null;
        }
        const updateEvents = actionItem.activity.filter(event =>
            event && event.event && typeof event.event === 'string' &&
            event.event.includes(`Workstream action ${actionItem.actionItemNumber} is updated`)
        );
        if (updateEvents.length === 0) return null;

        updateEvents.sort((a, b) => {
            const dateA = a.eventDate ? new Date(a.eventDate).getTime() : 0;
            const dateB = b.eventDate ? new Date(b.eventDate).getTime() : 0;
            return dateB - dateA;
        });
        return updateEvents[0] || null;
    };

    const getDynamicWorkstreamActionExportRow = (actionItem, allUsersData, allWorkstreamsDefinitions, sourceInitiatives, actionItemInitialCols, actionItemVisibleCols) => {
        const rowData = {};

        if (!Array.isArray(actionItemVisibleCols) || !Array.isArray(actionItemInitialCols)) {
            console.error('actionItemVisibleCols or actionItemInitialCols is not an array.');
            return rowData;
        }

        if (!actionItem) return rowData;

        const ownerName = actionItem.owner ?
            `${actionItem.owner.firstName || ''} ${actionItem.owner.lastName || ''}`.trim() : '';

        const collaboratorUsers = actionItem.collaborator && Array.isArray(actionItem.collaborator) ?
            actionItem.collaborator.map(collab =>
                collab && typeof collab === 'object' && collab.firstName && collab.lastName
                    ? `${collab.firstName} ${collab.lastName}`
                    : ''
            ).filter(Boolean).join(', ') : '';

        let subworkstreamName = 'N/A';
        let workstreamName = 'N/A';

        if (actionItem.owner && actionItem.owner.subworkstream) {
            if (typeof actionItem.owner.subworkstream === 'object' && actionItem.owner.subworkstream.subworkstreamName) {
                subworkstreamName = actionItem.owner.subworkstream.subworkstreamName;
                if (actionItem.owner.subworkstream.workstream) {
                    workstreamName = actionItem.owner.subworkstream.workstream.workstreamName || 'N/A';
                }
            }
        } else if (actionItem.owner && actionItem.owner.workstream) {
            workstreamName = actionItem.owner.workstream.workstreamName || 'N/A';
        }

        actionItemVisibleCols.forEach(columnKey => {
            const columnDef = actionItemInitialCols.find(col => col.key === columnKey);

            if (!columnDef) {
                rowData[columnDef?.title || columnKey] = 'N/A';
                return;
            }

            switch (columnDef.key) {
                case 'actionItemNumber':
                    rowData[columnDef.title] = actionItem.actionItemNumber ? parseInt(actionItem.actionItemNumber) || '' : '';
                    break;
                case 'workstreamActionName':
                    rowData[columnDef.title] = actionItem.workstreamActionName || actionItem.actionItemNumber || '';
                    break;
                case 'owner':
                    rowData[columnDef.title] = ownerName;
                    break;
                case 'collaborator':
                    rowData[columnDef.title] = collaboratorUsers;
                    break;
                case 'subworkstream':
                    rowData[columnDef.title] = subworkstreamName;
                    break;
                case 'workstream':
                    rowData[columnDef.title] = workstreamName;
                    break;
                case 'selectedForToMeeting':
                    rowData[columnDef.title] = actionItem.selectedForToMeeting ? 'Yes' : 'No';
                    break;
                case 'status':
                    rowData[columnDef.title] = actionItem.status || '';
                    break;
                case 'planningSummary':
                    rowData[columnDef.title] = determinePlanningSummary ? determinePlanningSummary(actionItem) || '' : '';
                    break;
                case 'comment':
                    rowData[columnDef.title] = actionItem.comment || '';
                    break;
                case 'dueDate':
                    rowData[columnDef.title] = actionItem.dueDate ? formatDateSafely(actionItem.dueDate) : '';
                    break;
                case 'Created on':
                    rowData[columnDef.title] = actionItem.created ? formatDateSafely(actionItem.created) : '';
                    break;
                case 'Updated on':
                    const latestUpdateEvent = getLatestUpdateEvent(actionItem);
                    rowData[columnDef.title] = latestUpdateEvent?.eventDate ? formatDateSafely(latestUpdateEvent.eventDate) : '';
                    break;
                case 'createdBy':
                    const createdByUser = actionItem.createdBy;
                    rowData[columnDef.title] = createdByUser ?
                        `${createdByUser.firstName || ''} ${createdByUser.lastName || ''}`.trim() : '';
                    break;
                case 'updatedBy':
                    const updatedBy = getLatestUpdateEvent(actionItem);
                    const updatedByName = updatedBy?.owner ?
                        `${updatedBy.owner.firstName || ''} ${updatedBy.owner.lastName || ''}`.trim() : null;
                    rowData[columnDef.title] = updatedByName || 'N/A';
                    break;
                default:
                    rowData[columnDef.title] = actionItem[columnDef.dataIndex] !== undefined ?
                        actionItem[columnDef.dataIndex] : '';
                    break;
            }
        });
        return rowData;
    };

    const findParentInitiative = (milestoneRecord, initiativesData) => {
        if (!milestoneRecord || !initiativesData || !Array.isArray(initiativesData)) return null;
        return initiativesData.find(i =>
            i && i.milestones && Array.isArray(i.milestones) &&
            i.milestones.some(ms => ms._id === milestoneRecord._id)
        );
    };

    const getLatestUserFromMilestoneHistory = (milestoneRecord, actionSubstring, allInitiativeData, allUserData) => {
        const parent = findParentInitiative(milestoneRecord, allInitiativeData);
        if (!parent || !parent.history || !Array.isArray(parent.history)) return 'N/A';

        const matches = parent.history.filter(entry =>
            entry && entry.action &&
            entry.action.includes &&
            entry.action.includes(actionSubstring) &&
            (entry.action.includes(`Milestone with Number ${milestoneRecord.milestoneNumber}`) ||
                entry.action.includes(`Milestone #${milestoneRecord.name}`))
        ).sort((a, b) => {
            const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return dateB - dateA;
        });

        const userFound = matches[0]?.user ?
            allUserData?.find(u => u._id === matches[0].user) : null;
        return userFound ? `${userFound.firstName || ''} ${userFound.lastName || ''}`.trim() : 'N/A';
    };

    const getDynamicMilestoneExportRow = (milestone, milestoneInitialCols, milestoneVisibleCols) => {
        const rowData = {};
        const ownerUser = milestone.Owner ?
            allUsersData.find(u => u._id === milestone.Owner) : null;
        if (!milestone || !Array.isArray(milestoneVisibleCols) || !Array.isArray(milestoneInitialCols)) {
            return rowData;
        }

        const parentInitiative = findParentInitiative(milestone, allInitiativesData);
        const initiativeOwnerInfo = parentInitiative?.initiativeOwnerInfo;

        milestoneVisibleCols.forEach(columnKey => {
            const columnDef = milestoneInitialCols.find(col => col.key === columnKey);

            if (!columnDef) {
                rowData[columnKey] = 'N/A';
                return;
            }

            switch (columnDef.key) {
                case 'milestoneNumber':
                    rowData[columnDef.title] = milestone.milestoneNumber ? parseInt(milestone.milestoneNumber) || '' : '';
                    break;
                case 'initiativeNumber':
                    rowData[columnDef.title] = parentInitiative?.initiativeNumber ?
                        parseInt(parentInitiative.initiativeNumber) || '' : '';
                    break;
                case 'initiativeName':
                    rowData[columnDef.title] = parentInitiative?.initiativeName || '';
                    break;
                case 'milestoneNumber - name':
                    rowData[columnDef.title] = `${milestone.milestoneNumber || ''} - ${milestone.name || ''}`;
                    break;
                case 'Milestone Status': rowData[columnDef.title] = milestone.status || ''; break;
                case 'initiativeOwnerInfo':
                    rowData[columnDef.title] = processPersonArray(initiativeOwnerInfo);
                    break;
                case 'Owner':
                    rowData[columnDef.title] = ownerUser ?
                        `${ownerUser.firstName || ''} ${ownerUser.lastName || ''}`.trim() : '';
                    break;
                case 'Work starting date':
                    rowData[columnDef.title] = milestone.startDate ? formatDateSafely(milestone.startDate) : '';
                    break;
                case 'Milestone completion date':
                    rowData[columnDef.title] = milestone.endDate ? formatDateSafely(milestone.endDate) : '';
                    break;
                case 'Milestone Planning summary':
                    rowData[columnDef.title] = getMilestonePlanningSummary ? getMilestonePlanningSummary(milestone, parentInitiative) || '' : '';
                    break;
                case 'Planning summary (simplified)':
                    rowData[columnDef.title] = getSimplifiedPlanningSummary ? getSimplifiedPlanningSummary(milestone, parentInitiative) || '' : '';
                    break;
                case 'Updated On':
                    const parent = findParentInitiative(milestone, allInitiativesData);
                    const historyEntries = parent?.history || [];
                    const matchingEntries = historyEntries
                        .filter(entry =>
                            entry && entry.action &&
                            entry.action.includes &&
                            entry.action.includes(`Milestone with Number ${milestone.milestoneNumber} has been updated.`)
                        )
                        .sort((a, b) => {
                            const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                            const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                            return dateB - dateA;
                        });

                    const latestEntry = matchingEntries[0];
                    rowData[columnDef.title] = latestEntry?.timestamp ? formatDateSafely(latestEntry.timestamp) : '';
                    break;
                case 'Created On':
                    rowData[columnDef.title] = milestone.created ? formatDateSafely(milestone.created) : '';
                    break;
                case 'Updated by':
                    const updatedBy = getLatestUserFromMilestoneHistory(milestone, 'updated', allInitiativesData, allUsersData);
                    rowData[columnDef.title] = updatedBy;
                    break;
                case 'Created by':
                    const createdBy = getLatestUserFromMilestoneHistory(milestone, 'created', allInitiativesData, allUsersData);
                    rowData[columnDef.title] = createdBy;
                    break;
                case 'Change in Milestone completion date (binned, current vs last week)':
                    rowData[columnDef.title] = getMilestoneBinValue ?
                        getMilestoneBinValue(milestone, parentInitiative) || '' : '';
                    break;
                case 'workstreamSponsorWorkstream':
                    rowData[columnDef.title] = processWorkstreamArray(parentInitiative?.workstreamSponsorWorkstream) || '';
                    break;
                case 'workstreamLeadSubworkstream':
                    rowData[columnDef.title] = processSubworkstreamArray(parentInitiative?.workstreamLeadSubworkstream) || '';
                    break;
                default:
                    rowData[columnDef.title] = milestone[columnDef.dataIndex] !== undefined ?
                        milestone[columnDef.dataIndex] : '';
                    break;
            }
        });
        return rowData;
    };

    const getDynamicInitiativeExportRow = (initiative, initialColumns, visibleColumns) => {
        const rowData = {};

        if (!initiative || !Array.isArray(visibleColumns) || !Array.isArray(initialColumns)) {
            return rowData;
        }

        visibleColumns.forEach(columnKey => {
            const columnDef = initialColumns.find(col => col.key === columnKey);

            if (!columnDef) {
                rowData[`Unknown Column (${columnKey})`] = 'N/A';
                return;
            }

            switch (columnDef.key) {
                case 'initiativeNumber':
                    rowData[columnDef.title] = initiative.initiativeNumber ?
                        parseInt(initiative.initiativeNumber) || '' : '';
                    break;
                case 'initiativeName':
                    rowData[columnDef.title] = initiative.initiativeName || '';
                    break;
                case 'stage':
                    rowData[columnDef.title] = determineStage ? determineStage(initiative) : '';
                    break;
                case 'stageSimplified':
                    rowData[columnDef.title] = initiative.stage || '';
                    break;
                case 'weeklyStatus':
                    rowData[columnDef.title] = initiative.weeklyStatus || '';
                    break;
                case 'initiativeOwnerInfo':
                    rowData[columnDef.title] = processPersonArray(initiative.initiativeOwnerInfo);
                    break;
                case 'workstreamSponsorWorkstream':
                    rowData[columnDef.title] = processWorkstreamArray(initiative.workstreamSponsorWorkstream);
                    break;
                case 'sectorInfo':
                    rowData[columnDef.title] = processSectorArray(initiative?.sectorInfo);
                    break;
                case 'subsectorInfo':
                    rowData[columnDef.title] = processSectorArray(initiative?.subsectorInfo);
                    break;
                case 'initiativeCategory':
                    rowData[columnDef.title] = initiative.initiativeCategory || '';
                    break;
                case 'segment':
                    rowData[columnDef.title] = initiative.segment || '';
                    break;
                case 'crossFunctionalSupportWorkstreamInfo':
                    rowData[columnDef.title] = processCrossFunctionalWorkstreamArray(initiative.crossFunctionalSupportWorkstreamInfo);
                    break;
                case 'strategyInfo':
                    const strategyNamesString = initiative.strategyInfo && Array.isArray(initiative.strategyInfo)
                        ? initiative.strategyInfo.map((s) => s?.strategyName || '').filter(Boolean).join(', ')
                        : '';
                    rowData[columnDef.title] = strategyNamesString || 'No strategy';
                    break;
                case 'initiativeOwnerBackupInfo':
                    rowData[columnDef.title] = processPersonArray(initiative.initiativeOwnerBackupInfo);
                    break;
                case 'AllowOnlyTo':
                    rowData[columnDef.title] = initiative.AllowOnlyTo || '';
                    break;
                case 'Description':
                    rowData[columnDef.title] = initiative.description || '';
                    break;
                case 'hasFinancialImpact':
                    rowData[columnDef.title] = initiative.hasFinancialImpact === true ? 'Yes' : 'No';
                    break;
                case 'forSpecificCustomer':
                    rowData[columnDef.title] = initiative.forSpecificCustomer ? 'Yes' : 'No';
                    break;
                case 'segmentalPBT':
                    rowData[columnDef.title] = initiative.segmentalPBT ? 'Yes' : 'No';
                    break;
                case 'priority':
                    rowData[columnDef.title] = initiative.priority || '';
                    break;
                case 'selectedForToMeetingDescusion':
                    rowData[columnDef.title] = initiative.selectForTOMeeting ? 'Yes' : 'No';
                    break;
                case 'workstreamLeadInfo':
                    rowData[columnDef.title] = processPersonArray(initiative.workstreamLeadInfo);
                    break;
                case 'workstreamSponsorInfo':
                    rowData[columnDef.title] = processPersonArray(initiative.workstreamSponsorInfo);
                    break;
                case 'workstreamLeadSubworkstream':
                    rowData[columnDef.title] = processSubworkstreamArray(initiative.workstreamLeadSubworkstream);
                    break;
                case 'netRecurringBenefit':
                    rowData[columnDef.title] = calculateNetRecurringBenefit ?
                        calculateNetRecurringBenefit(initiative) || 0 : 0;
                    break;
                case 'oneTimeBenefit':
                    rowData[columnDef.title] = calculateOneTimeBenefit ?
                        calculateOneTimeBenefit(initiative) || 0 : 0;
                    break;
                case 'oneTimeImplementationCost':
                    rowData[columnDef.title] = calculateOneTimeImplementationCost ?
                        calculateOneTimeImplementationCost(initiative) || 0 : 0;
                    break;
                case 'L3 planned date':
                    rowData[columnDef.title] = l3PlanDate ? formatDateSafely(l3PlanDate(initiative)) : '';
                    break;
                case 'L4 planned date':
                    rowData[columnDef.title] = l4PlanDate ? formatDateSafely(l4PlanDate(initiative)) : '';
                    break;
                case 'Planning summary':
                    const planningSummary = InitiativePlanningSummary ?
                        InitiativePlanningSummary(initiative) : { planningStatus: '' };
                    rowData[columnDef.title] = planningSummary.planningStatus || '';
                    break;
                case 'Planning summary simplified':
                    rowData[columnDef.title] = planningSummarySimplified(initiative) || '';
                    break;
                case 'Workstream lead pending approval':
                    rowData[columnDef.title] = checkWorkstreamLeadApprovalStatus ?
                        checkWorkstreamLeadApprovalStatus(initiative, allHistoryData) : '';
                    break;
                case 'Workstream sponsor pending approval':
                    rowData[columnDef.title] = checkWorkstreamSponsorApprovalStatus ?
                        checkWorkstreamSponsorApprovalStatus(initiative, allHistoryData) : '';
                    break;
                case 'TO finance pending approval':
                    rowData[columnDef.title] = checkTOFinanceApprovalStatus ?
                        checkTOFinanceApprovalStatus(initiative, allHistoryData) : '';
                    break;
                case 'Strategy analyst pending approval':
                    rowData[columnDef.title] = checkStrategyAnalystApprovalStatus ?
                        checkStrategyAnalystApprovalStatus(initiative, allHistoryData) : '';
                    break;
                case 'Net recurring benefit range':
                    rowData[columnDef.title] = getNetRecurringBenefitCatagory ?
                        getNetRecurringBenefitCatagory(initiative) : '';
                    break;
                case 'Future implementation costs range':
                    rowData[columnDef.title] = getImplementationCostCatagory ?
                        getImplementationCostCatagory(initiative) : '';
                    break;
                case 'Pipeline weekly variance (L0+)':
                    rowData[columnDef.title] = getNetRecurringBenefitCatagoryL0Plus ?
                        getNetRecurringBenefitCatagoryL0Plus(initiative, selectedRange) : '';
                    break;
                case 'Pipeline weekly variance (L1+)':
                    rowData[columnDef.title] = getNetRecurringBenefitCatagoryL1Plus ?
                        getNetRecurringBenefitCatagoryL1Plus(initiative, selectedRange) : '';
                    break;
                case 'Pipeline weekly variance (L2+)':
                    rowData[columnDef.title] = getNetRecurringBenefitCatagoryL2Plus ?
                        getNetRecurringBenefitCatagoryL2Plus(initiative, selectedRange) : '';
                    break;
                case 'Pipeline weekly variance (L3+)':
                    rowData[columnDef.title] = getNetRecurringBenefitCatagoryL3Plus ?
                        getNetRecurringBenefitCatagoryL3Plus(initiative, selectedRange) : '';
                    break;
                case 'Pipeline weekly variance (L4+)':
                    rowData[columnDef.title] = getNetRecurringBenefitCatagoryL4Plus ?
                        getNetRecurringBenefitCatagoryL4Plus(initiative, selectedRange) : '';
                    break;
                case 'Actual KPI update check (for last completed and previous period)':
                    rowData[columnDef.title] = sortInitiativesByActuals ?
                        sortInitiativesByActuals(initiative, 'actualKpiUpdateCheck') : '';
                    break;
                case 'Actual impact update check (for last completed and previous period)':
                    rowData[columnDef.title] = sortInitiativesByActuals ?
                        sortInitiativesByActuals(initiative, 'actualImpactUpdateCheck') : '';
                    break;
                case '(FY23-24) Actual impact update check':
                    rowData[columnDef.title] = groupInitiativesByYearActual ?
                        groupInitiativesByYearActual(initiative, '(FY23-24) Actual impact update check') : '';
                    break;
                case '(FY24-25) Actual impact update check':
                    rowData[columnDef.title] = groupInitiativesByYearActual ?
                        groupInitiativesByYearActual(initiative, '(FY24-25) Actual impact update check') : '';
                    break;
                case 'L3 date compliance check (forecast vs planned date)':
                    rowData[columnDef.title] = getComplianceStatus(initiative, 'L3').join(', ');
                    break;
                case 'L4 date compliance check (forecast vs planned date)':
                    rowData[columnDef.title] = getComplianceStatus(initiative, 'L4').join(', ');
                    break;
                case 'L1 Original Planned Date':
                    rowData[columnDef.title] = formatDateSafely(L1OriginalPlanDate(initiative));
                    break;
                case 'L2 Original Planned Date':
                    rowData[columnDef.title] = formatDateSafely(L2OriginalPlanDate(initiative));
                    break;
                case 'L3 Original Planned Date':
                    rowData[columnDef.title] = formatDateSafely(L3OriginalPlanDate(initiative));
                    break;
                case 'L4 Original Planned Date':
                    rowData[columnDef.title] = formatDateSafely(L4OriginalPlanDate(initiative));
                    break;
                case 'L5 Original Planned Date':
                    rowData[columnDef.title] = formatDateSafely(L5OriginalPlanDate(initiative));
                    break;
                case 'L2 planned date':
                    rowData[columnDef.title] = l2PlanDate ? formatDateSafely(l2PlanDate(initiative)) : '';
                    break;
                case 'L5 planned date':
                    rowData[columnDef.title] = l5PlanDate ? formatDateSafely(l5PlanDate(initiative)) : '';
                    break;
                case 'L0 actual date':
                    rowData[columnDef.title] = L0PlanDate ? formatDateSafely(L0PlanDate(initiative)) : '';
                    break;
                case 'L1 actual date':
                    rowData[columnDef.title] = L1PlanDate ? formatDateSafely(L1PlanDate(initiative)) : '';
                    break;
                case 'L2 actual date':
                    rowData[columnDef.title] = l2ActualDate ? formatDateSafely(l2ActualDate(initiative)) : '';
                    break;
                case 'L3 actual date':
                    rowData[columnDef.title] = l3ActualDate ? formatDateSafely(l3ActualDate(initiative)) : '';
                    break;
                case 'L4 actual date':
                    rowData[columnDef.title] = l4ActualDate ? formatDateSafely(l4ActualDate(initiative)) : '';
                    break;
                case 'L5 actual date':
                    rowData[columnDef.title] = l5ActualDate ? formatDateSafely(l5ActualDate(initiative)) : '';
                    break;
                case 'Sub . for L1 approv . date':
                    rowData[columnDef.title] = submittedForL1ApprovalPlanDate ?
                        formatDateSafely(submittedForL1ApprovalPlanDate(initiative, allHistoryData)) : '';
                    break;
                case 'Sub . for L2 approv . date':
                    rowData[columnDef.title] = submittedForL2ApprovalPlanDate ?
                        formatDateSafely(submittedForL2ApprovalPlanDate(initiative, allHistoryData)) : '';
                    break;
                case 'Sub . for L3 approv . date':
                    rowData[columnDef.title] = submittedForL3ApprovalPlanDate ?
                        formatDateSafely(submittedForL3ApprovalPlanDate(initiative, allHistoryData)) : '';
                    break;
                case 'Sub . for L4 approv . date':
                    rowData[columnDef.title] = submittedForL4ApprovalPlanDate ?
                        formatDateSafely(submittedForL4ApprovalPlanDate(initiative, allHistoryData)) : '';
                    break;
                case 'Sub . for L5 approv . date':
                    rowData[columnDef.title] = submittedForL5ApprovalPlanDate ?
                        formatDateSafely(submittedForL5ApprovalPlanDate(initiative, allHistoryData)) : '';
                    break;
                case 'latestActualDateAllLGates':
                    rowData[columnDef.title] = LLatestActualDate ?
                        formatDateSafely(LLatestActualDate(initiative, allHistoryData)) : '';
                    break;
                case 'Number of attachments':
                    rowData[columnDef.title] = initiative.attachmentCount || 0;
                    break;
                case 'Has qualitative KPI?':
                    const hasQuantitativeKPI = getQualitativeKPI ? getQualitativeKPI(initiative) : false;
                    rowData[columnDef.title] = hasQuantitativeKPI ? 'No' : 'Yes';
                    break;
                case 'initiativeNumber - initiativeName':
                    rowData[columnDef.title] = `${initiative.initiativeNumber || ''} - ${initiative.initiativeName || ''}`;
                    break;
                case 'Cancelation Date':
                    rowData[columnDef.title] = getCancellationDate ? formatDateSafely(getCancellationDate(initiative)) : '';
                    break;
                default:
                    rowData[columnDef.title] = initiative[columnDef.dataIndex] !== undefined ?
                        initiative[columnDef.dataIndex] : '';
                    break;
            }
        });
        return rowData;
    };

    const wb = utils.book_new();

    // Determine the source of initiatives for dependent sheets
    const sourceInitiativesForDependentSheets = mainExportType === 'initiatives' ? mainExportData : allInitiativesData;

    // Ensure sourceInitiativesForDependentSheets is an array
    if (!Array.isArray(sourceInitiativesForDependentSheets)) {
        console.error('sourceInitiativesForDependentSheets is not an array');
        return;
    }

    // --- PRIMARY SHEET GENERATION (based on mainExportType) ---
    if (mainExportType === 'milestones' && mainExportData && Array.isArray(mainExportData) && mainExportData.length > 0) {
        const milestoneHeaders = milestoneInitialColumns
            .filter(col => milestoneVisibleColumns.includes(col.key))
            .map(col => col.title);

        if (groupBy && groupBy !== 'None' && !(Array.isArray(groupBy) && groupBy.length === 0)) {
            const groupedMilestones = groupMilestonesBy(mainExportData, groupBy, allUsersData);
            let allGroupedRows = [];
            allGroupedRows.push(milestoneHeaders);

            for (const groupKey in groupedMilestones) {
                const milestonesInGroup = groupedMilestones[groupKey];
                if (!Array.isArray(milestonesInGroup)) continue;

                const groupHeaderRow = [`${groupKey} (${milestonesInGroup.length})`];
                for (let i = 1; i < milestoneHeaders.length; i++) {
                    groupHeaderRow.push('');
                }
                allGroupedRows.push(groupHeaderRow);

                milestonesInGroup.forEach(milestone => {
                    const rowObject = getDynamicMilestoneExportRow(milestone, milestoneInitialColumns, milestoneVisibleColumns);
                    const row = milestoneHeaders.map(header => rowObject[header] || '');
                    allGroupedRows.push(row);
                });
            }
            const ws = utils.aoa_to_sheet(allGroupedRows);
            applyDateFormats(ws);
            utils.book_append_sheet(wb, ws, 'Milestones');
        } else {
            const milestonesSheetData = mainExportData.map(milestone => {
                const rowObject = getDynamicMilestoneExportRow(milestone, milestoneInitialColumns, milestoneVisibleColumns);
                return milestoneHeaders.map(header => rowObject[header] || '');
            });

            const sheetData = [milestoneHeaders, ...milestonesSheetData];
            const ws = utils.aoa_to_sheet(sheetData);
            applyDateFormats(ws);
            utils.book_append_sheet(wb, ws, 'Milestones');
        }
    } else if (mainExportType === 'workstreamactions' && mainExportData && Array.isArray(mainExportData) && mainExportData.length > 0) {
        const actionHeaders = actionItemInitialColumn
            .filter(col => actionItemVisibleColumns.includes(col.key))
            .map(col => col.title);

        if (groupBy && groupBy !== 'None' && !(Array.isArray(groupBy) && groupBy.length === 0)) {
            const groupedWorkstreamActions = groupWorkstreamActionsBy(
                mainExportData,
                groupBy,
                allUsersData
            );
            let allGroupedRows = [];
            allGroupedRows.push(actionHeaders);

            for (const groupKey in groupedWorkstreamActions) {
                const actionItemsInGroup = groupedWorkstreamActions[groupKey];
                if (!Array.isArray(actionItemsInGroup)) continue;

                const groupHeaderRow = [`${groupKey} (${actionItemsInGroup.length})`];
                for (let i = 1; i < actionHeaders.length; i++) {
                    groupHeaderRow.push('');
                }
                allGroupedRows.push(groupHeaderRow);

                actionItemsInGroup.forEach(item => {
                    const rowObject = getDynamicWorkstreamActionExportRow(item, allUsersData, allWorkstreamsDefinitions, sourceInitiativesForDependentSheets, actionItemInitialColumn, actionItemVisibleColumns);
                    const row = actionHeaders.map(header => rowObject[header] || '');
                    allGroupedRows.push(row);
                });
            }
            const ws = utils.aoa_to_sheet(allGroupedRows);
            applyDateFormats(ws);
            utils.book_append_sheet(wb, ws, 'Workstream Actions');
        } else {
            const actionSheetData = mainExportData.map(item => {
                const rowObject = getDynamicWorkstreamActionExportRow(item, allUsersData, allWorkstreamsDefinitions, sourceInitiativesForDependentSheets, actionItemInitialColumn, actionItemVisibleColumns);
                return actionHeaders.map(header => rowObject[header] || '');
            });

            const sheetData = [actionHeaders, ...actionSheetData];
            const ws = utils.aoa_to_sheet(sheetData);
            applyDateFormats(ws);
            utils.book_append_sheet(wb, ws, 'Workstream Actions');
        }
    } else {
        // Default or 'initiatives' export
        if (!Array.isArray(mainExportData)) {
            mainExportData = [];
        }

        if (groupBy && groupBy !== 'None' && !(Array.isArray(groupBy) && groupBy.length === 0)) {
            const groupedInitiatives = groupInitiativesBy(
                mainExportData,
                groupBy,
                determineStage,
                checkTOFinanceApprovalStatus,
                checkWorkstreamLeadApprovalStatus,
                checkWorkstreamSponsorApprovalStatus,
                getNetRecurringBenefitCatagory,
                InitiativePlanningSummary,
                L0PlanDate, L1PlanDate, l2ActualDate, l2PlanDate, l3ActualDate, l3PlanDate,
                l4ActualDate, l4PlanDate, l5ActualDate, l5PlanDate,
                submittedForL1ApprovalPlanDate, submittedForL2ApprovalPlanDate,
                submittedForL3ApprovalPlanDate, submittedForL4ApprovalPlanDate,
                submittedForL5ApprovalPlanDate, getNetRecurringBenefitCatagoryL0Plus,
                getNetRecurringBenefitCatagoryL1Plus, getNetRecurringBenefitCatagoryL2Plus,
                getNetRecurringBenefitCatagoryL3Plus, getNetRecurringBenefitCatagoryL4Plus,
                getComplianceStatusForGrouping, sortInitiativesByActuals,
                checkStrategyAnalystApprovalStatus, allUsersData,
                allWorkstreamsDefinitions,
                allHistoryData, selectedRange, sortInitiativesByActualImpact,
                getImplementationCostCatagory,
                initialColumns,
                visibleColumns
            );

            let allGroupedRows = [];
            const headers = initialColumns
                .filter(col => visibleColumns.includes(col.key))
                .map(col => col.title);

            allGroupedRows.push(headers);

            if (groupedInitiatives && groupedInitiatives.groupedData) {
                for (const [groupKey, initiativesInGroup] of Object.entries(groupedInitiatives.groupedData)) {
                    if (!Array.isArray(initiativesInGroup)) continue;

                    const cleanGroupKey = groupKey.replace(/\(Total: \d+\)/, '').trim();
                    const groupHeaderRow = [`${cleanGroupKey} (${initiativesInGroup.length})`];

                    for (let i = 1; i < headers.length; i++) {
                        groupHeaderRow.push('');
                    }
                    allGroupedRows.push(groupHeaderRow);

                    initiativesInGroup.forEach(initiative => {
                        const rowObject = getDynamicInitiativeExportRow(initiative, initialColumns, visibleColumns);
                        const orderedRow = headers.map(header => rowObject[header] || '');
                        allGroupedRows.push(orderedRow);
                    });
                }
            }

            const ws = utils.aoa_to_sheet(allGroupedRows);
            applyDateFormats(ws);
            utils.book_append_sheet(wb, ws, 'Initiatives');
        } else {
            const headers = initialColumns
                .filter(col => visibleColumns.includes(col.key))
                .map(col => col.title);

            const initiativesSheetData = mainExportData.map(initiative => {
                const rowObject = getDynamicInitiativeExportRow(initiative, initialColumns, visibleColumns);
                return headers.map(header => rowObject[header] || '');
            });

            const sheetData = [headers, ...initiativesSheetData];
            const initiativesSheet = utils.aoa_to_sheet(sheetData);
            applyDateFormats(initiativesSheet);
            utils.book_append_sheet(wb, initiativesSheet, 'Initiatives');
        }

        // --- All Milestones Sheet ---
        const allMilestones = sourceInitiativesForDependentSheets.flatMap(i =>
            (i && i.milestones && Array.isArray(i.milestones)) ? i.milestones : []
        );

        if (allMilestones.length > 0) {
            const allMilestoneHeaders = milestoneInitialColumns.map(col => col.title);
            const processedMilestones = allMilestones.map(milestone => {
                const rowObject = getDynamicMilestoneExportRow(milestone, milestoneInitialColumns, milestoneInitialColumns.map(col => col.key));
                return allMilestoneHeaders.map(header => rowObject[header] || '');
            });
            const sheetData = [allMilestoneHeaders, ...processedMilestones];
            const wsAllMilestones = utils.aoa_to_sheet(sheetData);
            applyDateFormats(wsAllMilestones);
            utils.book_append_sheet(wb, wsAllMilestones, 'All Milestones');
        } else {
            const headers = milestoneInitialColumns.map(col => col.title);
            const wsAllMilestones = utils.aoa_to_sheet([headers]);
            utils.book_append_sheet(wb, wsAllMilestones, 'All Milestones');
        }

        // --- Workstreams Sheet ---
        if (allWorkstreamsDefinitions && allWorkstreamsDefinitions.length > 0) {
            const processedWorkstreams = allWorkstreamsDefinitions.map(({ _id, ...rest }) => rest);
            utils.book_append_sheet(wb, utils.json_to_sheet(processedWorkstreams), 'Workstreams');
        }

        // --- Dynamic Header Generation for Impact and KPI sheets ---
        const dataStartYear = 2023;
        const dataEndYear = 2028;
        const monthsOrder = [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        for (let year = dataStartYear; year <= dataEndYear; year++) {
            let currentMonthIndex = (year === dataStartYear) ? 6 : 0;
            let lastMonthIndex = (year === dataEndYear) ? 5 : 11;

            for (let i = currentMonthIndex; i <= lastMonthIndex; i++) {
                monthsOrder.push({ name: monthNames[i], year: year });
            }
        }

        const yearsForHeaders = [];
        for (let i = 0; i < 5; i++) {
            yearsForHeaders.push(dataStartYear + i);
        }

        const quartersForHeaders = [];
        const quarterPrefixes = ['Q1', 'Q2', 'Q3', 'Q4'];
        let currentQuarterYear = dataStartYear;
        for (let i = 0; i < 20; i++) {
            quartersForHeaders.push(`${quarterPrefixes[i % 4]}-${currentQuarterYear}`);
            if ((i + 1) % 4 === 0) {
                currentQuarterYear++;
            }
        }

        const maxMonthlyDataPoints = 60;
        const dynamicMonths = monthsOrder.slice(0, maxMonthlyDataPoints);

        // --- Impact Sheet ---
        const allImpacts = [];
        sourceInitiativesForDependentSheets.forEach((initiative) => {
            if (initiative && initiative.impact && Array.isArray(initiative.impact) && initiative.impact.length > 0) {
                initiative.impact.forEach(imp => {
                    if (imp) {
                        allImpacts.push({
                            ...imp,
                            Segment: initiative?.initiativeCategory,
                            Subsegment: initiative?.segment,
                            Sector: getSectorName(imp),
                            Subsector: getSubsectorSpecificName(imp),
                            initiativeNumber: parseInt(initiative.initiativeNumber) || 0,
                            accountableWorkstream: initiative.workstreamSponsorWorkstream?.[0]?.workstreamName || '',
                            subworkstream: initiative.workstreamLeadSubworkstream?.[0]?.subworkstreamName || '',
                        });
                    }
                });
            }
        });

        if (allImpacts.length > 0) {
            const impactSheetData = [];

            const impactHeaderRow = [
                'Initiative',
                'impactNumber',
                'frequencyAndDirection',
                'typeOfImpact',
                'Segment',
                'Subsegment',
                'Sector',
                'Subsector',
                'Accountable Workstream',
                'Accountable Subworkstream',
                'Plan annualized impact',
                'Actual annualized impact',
                'Forecast annualized impact',
                'Estimate annualized impact',
                'Purpose'
            ];

            dynamicMonths.forEach(month => {
                impactHeaderRow.push(`${month.name}-${month.year}`);
            });

            yearsForHeaders.forEach(year => {
                impactHeaderRow.push(`Year ${year}/${year + 1}`);
            });

            quartersForHeaders.forEach(quarter => {
                const [qPrefix, qYear] = quarter.split('-');
                impactHeaderRow.push(`${qPrefix}-${qYear}/${parseInt(qYear) + 1}`);
            });

            impactSheetData.push(impactHeaderRow);

            allImpacts.forEach((impact) => {
                const purposeTypes = impact.typeOfImpact === 'Qualitative Impact' ?
                    ['Plan', 'Actual'] : ['Plan', 'Actual', 'Forecast', 'Estimate'];

                purposeTypes.forEach(type => {
                    const row = new Array(impactHeaderRow.length).fill('');

                    row[0] = impact.initiativeNumber || '';
                    row[1] = impact.impactNumber ? parseInt(impact.impactNumber) || '' : '';
                    row[2] = impact.frequencyAndDirection || '';
                    row[3] = impact.typeOfImpact || '';
                    row[4] = impact.Segment || '';
                    row[5] = impact.Subsegment || '';
                    row[6] = impact.Sector || '';
                    row[7] = impact.Subsector || '';
                    row[8] = impact.accountableWorkstream || '';
                    row[9] = impact.subworkstream || '';
                    row[14] = type;

                    if (impact.typeOfImpact === 'Qualitative Impact') {
                        if (type === 'Plan') {
                            row[10] = impact.annualizedValues?.planqualitativeimpact || '';
                        } else if (type === 'Actual') {
                            row[11] = impact.annualizedValues?.actualqualitativeimpact || '';
                        }
                        row[12] = '';
                        row[13] = '';
                    } else {
                        row[10] = impact.annualizedValues?.plan !== undefined ?
                            impact.annualizedValues.plan : '';
                        row[11] = impact.annualizedValues?.actual !== undefined ?
                            impact.annualizedValues.actual : '';
                        row[12] = impact.annualizedValues?.forecast !== undefined ?
                            impact.annualizedValues.forecast : '';
                        row[13] = impact.annualizedValues?.estimate !== undefined ?
                            impact.annualizedValues.estimate : '';
                    }

                    let currentDataColIndex = 15;

                    dynamicMonths.forEach((month, monthIdx) => {
                        let value = '';
                        if (impact.typeOfImpact === 'Qualitative Impact') {
                            if (type === 'Plan') {
                                value = impact.monthlyPlanQualitativeImpact?.[monthIdx] !== undefined ?
                                    impact.monthlyPlanQualitativeImpact[monthIdx] : '';
                            } else if (type === 'Actual') {
                                value = impact.monthlyActualQualitativeImpact?.[monthIdx] !== undefined ?
                                    impact.monthlyActualQualitativeImpact[monthIdx] : '';
                            }
                        } else {
                            if (type === 'Plan') {
                                value = impact.monthlyPlan?.[monthIdx] !== undefined ?
                                    impact.monthlyPlan[monthIdx] : '';
                            } else if (type === 'Actual') {
                                value = impact.monthlyActual?.[monthIdx] !== undefined ?
                                    impact.monthlyActual[monthIdx] : '';
                            } else if (type === 'Forecast') {
                                value = impact.monthlyForecast?.[monthIdx] !== undefined ?
                                    impact.monthlyForecast[monthIdx] : '';
                            } else if (type === 'Estimate') {
                                value = impact.monthlyEstimate?.[monthIdx] !== undefined ?
                                    impact.monthlyEstimate[monthIdx] : '';
                            }
                        }
                        row[currentDataColIndex++] = value;
                    });

                    yearsForHeaders.forEach((year, yearIdx) => {
                        let value = '';
                        if (impact.typeOfImpact === 'Qualitative Impact') {
                            if (type === 'Plan') {
                                value = impact.annuallyPlanQualitativeImpact?.[yearIdx] !== undefined ?
                                    impact.annuallyPlanQualitativeImpact[yearIdx] : '';
                            } else if (type === 'Actual') {
                                value = impact.annuallyActualQualitativeImpact?.[yearIdx] !== undefined ?
                                    impact.annuallyActualQualitativeImpact[yearIdx] : '';
                            }
                        } else {
                            if (type === 'Plan') {
                                value = impact.annuallyPlan?.[yearIdx] !== undefined ?
                                    impact.annuallyPlan[yearIdx] : '';
                            } else if (type === 'Actual') {
                                value = impact.annuallyActual?.[yearIdx] !== undefined ?
                                    impact.annuallyActual[yearIdx] : '';
                            } else if (type === 'Forecast') {
                                value = impact.annuallyForecast?.[yearIdx] !== undefined ?
                                    impact.annuallyForecast[yearIdx] : '';
                            } else if (type === 'Estimate') {
                                value = impact.annuallyEstimate?.[yearIdx] !== undefined ?
                                    impact.annuallyEstimate[yearIdx] : '';
                            }
                        }
                        row[currentDataColIndex++] = value;
                    });

                    quartersForHeaders.forEach((quarter, quarterIdx) => {
                        let value = '';
                        if (impact.typeOfImpact === 'Qualitative Impact') {
                            if (type === 'Plan') {
                                value = impact.quarterlyPlanQualitativeImpact?.[quarterIdx] !== undefined ?
                                    impact.quarterlyPlanQualitativeImpact[quarterIdx] : '';
                            } else if (type === 'Actual') {
                                value = impact.quarterlyActualQualitativeImpact?.[quarterIdx] !== undefined ?
                                    impact.quarterlyActualQualitativeImpact[quarterIdx] : '';
                            }
                        } else {
                            if (type === 'Plan') {
                                value = impact.quarterlyPlan?.[quarterIdx] !== undefined ?
                                    impact.quarterlyPlan[quarterIdx] : '';
                            } else if (type === 'Actual') {
                                value = impact.quarterlyActual?.[quarterIdx] !== undefined ?
                                    impact.quarterlyActual[quarterIdx] : '';
                            } else if (type === 'Forecast') {
                                value = impact.quarterlyForecast?.[quarterIdx] !== undefined ?
                                    impact.quarterlyForecast[quarterIdx] : '';
                            } else if (type === 'Estimate') {
                                value = impact.quarterlyEstimate?.[quarterIdx] !== undefined ?
                                    impact.quarterlyEstimate[quarterIdx] : '';
                            }
                        }
                        row[currentDataColIndex++] = value;
                    });

                    impactSheetData.push(row);
                });
            });

            const impactSheet = utils.aoa_to_sheet(impactSheetData);
            utils.book_append_sheet(wb, impactSheet, 'Impact');
        }

        // --- KPI Sheet ---
        const allKPIs = [];
        sourceInitiativesForDependentSheets.forEach((initiative) => {
            if (initiative && initiative.KPI && Array.isArray(initiative.KPI) && initiative.KPI.length > 0) {
                initiative.KPI.forEach(kpiItem => {
                    if (kpiItem && typeof kpiItem === 'object') {
                        allKPIs.push({
                            ...kpiItem,
                            Segment: initiative?.initiativeCategory,
                            Subsegment: initiative?.segment,
                            Sector: getSectorName(kpiItem),
                            Subsector: getSubsectorSpecificName(kpiItem),
                            initiativeNumber: parseInt(initiative.initiativeNumber) || 0,
                            workstream: initiative.workstreamSponsorWorkstream?.[0]?.workstreamName || '',
                            subworkstream: initiative.workstreamLeadSubworkstream?.[0]?.subworkstreamName || '',
                        });
                    }
                });
            }
        });

        if (allKPIs.length > 0) {
            const kpiSheetData = [];

            const kpiHeaderRow = [
                'Initiative Number',
                'KPINumber',
                'StartAmortizationOrDepreciation',
                'TypeOfKPI',
                'Segment',
                'Subsegment',
                'Sector',
                'Subsector',
                'UniversalKPI',
                'unitOfMeasurement',
                'aggregationMode',
                'isFinancial',
                'Accountable Workstream',
                'Accountable Subworkstream',
                'Baseline',
                'Purpose'
            ];

            dynamicMonths.forEach(month => {
                kpiHeaderRow.push(`${month.name}-${month.year}`);
            });

            yearsForHeaders.forEach(year => {
                kpiHeaderRow.push(`Year ${year}/${year + 1}`);
            });

            quartersForHeaders.forEach(quarter => {
                const [qPrefix, qYear] = quarter.split('-');
                kpiHeaderRow.push(`${qPrefix}-${qYear}/${parseInt(qYear) + 1}`);
            });

            kpiSheetData.push(kpiHeaderRow);

            allKPIs.forEach((kpiData) => {
                const purposeTypes = kpiData.isFinancial === true ?
                    ['Plan', 'Actual', 'Forecast', 'Estimate'] : ['Plan', 'Actual'];

                purposeTypes.forEach(type => {
                    const row = new Array(kpiHeaderRow.length).fill('');

                    let currentDataColIndex = 0;

                    row[currentDataColIndex++] = kpiData.initiativeNumber || '';
                    row[currentDataColIndex++] = kpiData.KPINumber ? parseInt(kpiData.KPINumber) || '' : '';
                    row[currentDataColIndex++] = kpiData.StartAmortizationOrDepreciation || '';
                    row[currentDataColIndex++] = kpiData.TypeOfKPI || '';
                    row[currentDataColIndex++] = kpiData.Segment || '';
                    row[currentDataColIndex++] = kpiData.Subsegment || '';
                    row[currentDataColIndex++] = kpiData.Sector || '';
                    row[currentDataColIndex++] = kpiData.Subsector || '';
                    row[currentDataColIndex++] = kpiData.UniversalKPI || '';
                    row[currentDataColIndex++] = kpiData.unitOfMeasurement || '';
                    row[currentDataColIndex++] = kpiData.aggregationMode || '';
                    row[currentDataColIndex++] = kpiData.isFinancial !== undefined ?
                        (kpiData.isFinancial ? 'Yes' : 'No') : '';
                    row[currentDataColIndex++] = kpiData.workstream || '';
                    row[currentDataColIndex++] = kpiData.subworkstream || '';
                    row[currentDataColIndex++] = kpiData.baseline || '';
                    row[currentDataColIndex++] = type;

                    dynamicMonths.forEach((month, monthIdx) => {
                        let value = '';
                        if (kpiData.isFinancial === true) {
                            if (type === 'Plan') {
                                value = kpiData.monthlyPlan?.[monthIdx] !== undefined ?
                                    kpiData.monthlyPlan[monthIdx] : '';
                            } else if (type === 'Actual') {
                                value = kpiData.monthlyActual?.[monthIdx] !== undefined ?
                                    kpiData.monthlyActual[monthIdx] : '';
                            } else if (type === 'Forecast') {
                                value = kpiData.monthlyForecast?.[monthIdx] !== undefined ?
                                    kpiData.monthlyForecast[monthIdx] : '';
                            } else if (type === 'Estimate') {
                                value = kpiData.monthlyEstimate?.[monthIdx] !== undefined ?
                                    kpiData.monthlyEstimate[monthIdx] : '';
                            }
                        } else {
                            if (type === 'Plan') {
                                value = kpiData.monthlynonFinancialKPIPlan?.[monthIdx] !== undefined ?
                                    kpiData.monthlynonFinancialKPIPlan[monthIdx] : '';
                            } else if (type === 'Actual') {
                                value = kpiData.monthlynonFinancialKPIActual?.[monthIdx] !== undefined ?
                                    kpiData.monthlynonFinancialKPIActual[monthIdx] : '';
                            }
                        }
                        row[currentDataColIndex++] = value;
                    });

                    yearsForHeaders.forEach((year, yearIdx) => {
                        let value = '';
                        if (kpiData.isFinancial === true) {
                            if (type === 'Plan') {
                                value = kpiData.annuallyPlan?.[yearIdx] !== undefined ?
                                    kpiData.annuallyPlan[yearIdx] : '';
                            } else if (type === 'Actual') {
                                value = kpiData.annuallyActual?.[yearIdx] !== undefined ?
                                    kpiData.annuallyActual[yearIdx] : '';
                            } else if (type === 'Forecast') {
                                value = kpiData.annuallyForecast?.[yearIdx] !== undefined ?
                                    kpiData.annuallyForecast[yearIdx] : '';
                            } else if (type === 'Estimate') {
                                value = kpiData.annuallyEstimate?.[yearIdx] !== undefined ?
                                    kpiData.annuallyEstimate[yearIdx] : '';
                            }
                        } else {
                            if (type === 'Plan') {
                                value = kpiData.annuallynonFinancialKPIPlan?.[yearIdx] !== undefined ?
                                    kpiData.annuallynonFinancialKPIPlan[yearIdx] : '';
                            } else if (type === 'Actual') {
                                value = kpiData.annuallynonFinancialKPIActual?.[yearIdx] !== undefined ?
                                    kpiData.annuallynonFinancialKPIActual[yearIdx] : '';
                            }
                        }
                        row[currentDataColIndex++] = value;
                    });

                    quartersForHeaders.forEach((quarter, quarterIdx) => {
                        let value = '';
                        if (kpiData.isFinancial === true) {
                            if (type === 'Plan') {
                                value = kpiData.quarterlyPlan?.[quarterIdx] !== undefined ?
                                    kpiData.quarterlyPlan[quarterIdx] : '';
                            } else if (type === 'Actual') {
                                value = kpiData.quarterlyActual?.[quarterIdx] !== undefined ?
                                    kpiData.quarterlyActual[quarterIdx] : '';
                            } else if (type === 'Forecast') {
                                value = kpiData.quarterlyForecast?.[quarterIdx] !== undefined ?
                                    kpiData.quarterlyForecast[quarterIdx] : '';
                            } else if (type === 'Estimate') {
                                value = kpiData.quarterlyEstimate?.[quarterIdx] !== undefined ?
                                    kpiData.quarterlyEstimate[quarterIdx] : '';
                            }
                        } else {
                            if (type === 'Plan') {
                                value = kpiData.quarterlynonFinancialKPIPlan?.[quarterIdx] !== undefined ?
                                    kpiData.quarterlynonFinancialKPIPlan[quarterIdx] : '';
                            } else if (type === 'Actual') {
                                value = kpiData.quarterlynonFinancialKPIActual?.[quarterIdx] !== undefined ?
                                    kpiData.quarterlynonFinancialKPIActual[quarterIdx] : '';
                            }
                        }
                        row[currentDataColIndex++] = value;
                    });

                    kpiSheetData.push(row);
                });
            });

            const kpiSheet = utils.aoa_to_sheet(kpiSheetData);
            utils.book_append_sheet(wb, kpiSheet, 'KPI');
        }

        // --- Attachment Sheet ---
        const allAttachments = allInitiativesData.flatMap(initiative => {
            if (!initiative || !Array.isArray(initiative.attachment) || initiative.attachment.length === 0) return [];

            return initiative.attachment.map(att => {
                if (!att) return null;
                const fullPath = att.attachment || "";
                const pathWithoutTrailingChar = fullPath.replace(/, $/, '');
                const filename = pathWithoutTrailingChar.split('/').pop();

                return {
                    initiativeNumber: initiative.initiativeNumber ? parseInt(initiative.initiativeNumber) : "",
                    initiativeName: initiative.initiativeName || "",
                    attachment: filename || "",
                    typeOfAttachment: att.typeOfAttachment || "",
                };
            }).filter(Boolean);
        });

        if (allAttachments.length > 0) {
            const sheetData = [
                ["initiativeNumber", "initiativeName", "attachment", "typeOfAttachment"],
                ...allAttachments.map(att => [
                    att.initiativeNumber,
                    att.initiativeName,
                    att.attachment,
                    att.typeOfAttachment,
                ]),
            ];

            utils.book_append_sheet(wb, utils.aoa_to_sheet(sheetData), "Attachments");
        }

        // --- Business Case Sheet ---
        const allBusinessCases = allInitiativesData
            .filter(initiative => initiative?.businessCase && typeof initiative.businessCase === "object")
            .map(initiative => {
                const businessCase = initiative.businessCase || {};

                return {
                    initiativeNumber: initiative.initiativeNumber ? parseInt(initiative.initiativeNumber) : "",
                    initiativeName: initiative.initiativeName || "",
                    baselineValue: businessCase.baselineValue || "",
                    baselineMeasureAndUnit: businessCase.baselineMeasureAndUnit || "",
                    baselineTargetValue: businessCase.baselineTargetValue || "",
                    implementationCosts: businessCase.implementationCosts || "",
                    keyAssumptions: businessCase.keyAssumptions || "",
                    MoneyStepAndMeasureSuccess: businessCase.MoneyStepAndMeasureSuccess || "",
                    NetRecuringBenefit: businessCase.NetRecuringBenefit || "",
                    oneTimeBenefit: businessCase.oneTimeBenefit || ""
                };
            });

        if (allBusinessCases.length > 0) {
            let sheetData = [];
            sheetData.push([
                "initiativeNumber",
                "initiativeName",
                "baselineValue",
                "baselineMeasureAndUnit",
                "baselineTargetValue",
                "implementationCosts",
                "keyAssumptions",
                "MoneyStepAndMeasureSuccess",
                "NetRecuringBenefit",
                "oneTimeBenefit"
            ]);

            allBusinessCases.forEach(bc => {
                sheetData.push([
                    bc.initiativeNumber,
                    bc.initiativeName,
                    bc.baselineValue,
                    bc.baselineMeasureAndUnit,
                    bc.baselineTargetValue,
                    bc.implementationCosts,
                    bc.keyAssumptions,
                    bc.MoneyStepAndMeasureSuccess,
                    bc.NetRecuringBenefit,
                    bc.oneTimeBenefit
                ]);
            });

            utils.book_append_sheet(wb, utils.aoa_to_sheet(sheetData), "Business Cases");
        }

        // --- Timeline Sheet ---
        const allTimelines = sourceInitiativesForDependentSheets.flatMap(i =>
            (i && i.timeline && Array.isArray(i.timeline)) ? i.timeline : []
        );

        if (allTimelines.length > 0) {
            const processedTimelines = allTimelines.map(t => {
                if (!t) return null;
                const { _id, date, ...rest } = t;
                const parentInitiative = sourceInitiativesForDependentSheets.find(i =>
                    i && i.timeline && Array.isArray(i.timeline) &&
                    i.timeline.some(tt => tt._id === t._id)
                );
                return {
                    ...rest,
                    datePlan: date?.plan ? formatDateSafely(date.plan) : '',
                    dateActual: date?.actual ? formatDateSafely(date.actual) : '',
                    initiativeNumber: parentInitiative?.initiativeNumber ?
                        parseInt(parentInitiative.initiativeNumber) || '' : '',
                };
            }).filter(Boolean);

            if (processedTimelines.length > 0) {
                const ws = utils.json_to_sheet(processedTimelines);
                applyDateFormats(ws);
                utils.book_append_sheet(wb, ws, 'Timeline');
            }
        }

        // --- Approval Summary Sheet ---
        if (sourceInitiativesForDependentSheets && sourceInitiativesForDependentSheets.length > 0) {
            const processedApprovals = sourceInitiativesForDependentSheets.map(i => {
                if (!i) return null;

                const {
                    initiativeNumber,
                    approvalSummary,
                    hasFinancialImpact,
                    workstreamLeadInfo,
                    workstreamSponsorInfo,
                } = i;

                const parsedInitiativeNumber = initiativeNumber ? parseInt(initiativeNumber) : '';
                const approvalData = {};

                const strategyAnalyst = allUsersData?.find(u =>
                    u && u.role && u.role.name === 'Strategy Analyst'
                );
                const toFinance = allUsersData?.find(u =>
                    u && u.role && u.role.name === 'TO Finance'
                );

                if (approvalSummary && typeof approvalSummary === 'object') {
                    Object.entries(approvalSummary).forEach(([level, data]) => {
                        if (!data) return;

                        let approvers = [];

                        if (data.approvalStatus === 'Pending') {
                            if (level === 'L1') {
                                approvers = [
                                    processPersonArray(workstreamLeadInfo),
                                    `${strategyAnalyst?.firstName || ''} ${strategyAnalyst?.lastName || ''}`.trim(),
                                ];
                            } else if (['L2', 'L3', 'L4', 'L5'].includes(level)) {
                                approvers = [
                                    processPersonArray(workstreamLeadInfo),
                                    processPersonArray(workstreamSponsorInfo),
                                    hasFinancialImpact
                                        ? `${toFinance?.firstName || ''} ${toFinance?.lastName || ''}`.trim()
                                        : `${strategyAnalyst?.firstName || ''} ${strategyAnalyst?.lastName || ''}`.trim(),
                                ];
                            }
                        } else if (data.approvers && Array.isArray(data.approvers)) {
                            approvers = data.approvers.map((a) => {
                                if (!a || !a.approvedBy) return 'Unknown';
                                const u = allUsersData.find(u => u._id === a.approvedBy);
                                return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : 'Unknown';
                            });
                        }

                        approvalData[level] = `${data.approvalStatus || ''} (${approvers.filter(Boolean).join(', ')})`;
                    });
                }

                return {
                    initiativeNumber: parsedInitiativeNumber,
                    ...approvalData,
                };
            }).filter(Boolean);

            if (processedApprovals.length > 0) {
                utils.book_append_sheet(wb, utils.json_to_sheet(processedApprovals), 'Approval Summary');
            }
        }
    }

    // --- Final Write Operation ---
    if (wb.SheetNames.length > 0) {
        writeFile(wb, 'AllData.xlsx', { cellDates: false });
        console.log("File 'AllData.xlsx' should have been downloaded.");
    } else {
        console.warn("No sheets were added to the workbook. Nothing to download.");
    }
};

export default handleDownloadData;